// The prep editor: a meeting's prep page as sections you click straight into.
// Text saves 600 ms after the last keystroke (PrepSaver), the page is re-read
// every 8 seconds and right after the chat edits it, and a section you are in
// is never overwritten by either. Sections reorder by press and hold, delete
// with a six-second Undo, and "+" adds one.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX, type PointerEvent as ReactPointerEvent } from "react";
import type { PrepDoc } from "@breeze/core";
import { ApiError } from "../lib/api";
import { getPrepDoc, savePrepDoc } from "../lib/prepApi";
import { SectionDrag, type DragHost } from "../lib/prepDrag";
import { mergeRemote, NEW_SECTION_HTML, NEW_SECTION_TITLE, freshSectionId, sanitizeHtml, type Sec } from "../lib/prepEdit";
import { PrepSaver, type SaveStatus } from "../lib/prepSaver";
import { PlusIcon } from "./icons";
import { PrepSection } from "./PrepSection";

const POLL_MS = 3000;
const UNDO_MS = 6000;

interface PrepEditorProps {
  readonly slug: string;
  /** Bump to re-read the page now (the chat just edited it). */
  readonly refreshKey?: number;
  /** The server has no such prep. */
  readonly onMissing?: () => void;
  /** The prep's join link (a Teams or Zoom link, a phone number as `tel:`, or empty), as read. */
  readonly onJoin?: (join: string) => void;
  /** Hide the title block, for a page that shows its own. */
  readonly bare?: boolean;
}

type Load = "loading" | "ready" | "missing" | "error";

interface Undo {
  readonly sec: Sec;
  readonly index: number;
  readonly title: string;
}

function statusText(s: SaveStatus): string {
  if (s === "saving") return "Saving";
  if (s === "saved") return "Saved";
  if (s === "failed") return "Not saved. Retrying";
  return "";
}

function focusedSectionId(): string | null {
  const a = document.activeElement;
  if (!(a instanceof Element)) return null;
  return a.closest<HTMLElement>("[data-sec]")?.dataset.sec ?? null;
}

export function PrepEditor({ slug, refreshKey = 0, onMissing, onJoin, bare = false }: PrepEditorProps): JSX.Element {
  const [load, setLoad] = useState<Load>("loading");
  const [head, setHead] = useState<{ title: string; meta: string }>({ title: "", meta: "" });
  const [sections, setSectionsState] = useState<Sec[]>([]);
  const [drag, setDrag] = useState<{ order: string[]; id: string } | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [failed, setFailed] = useState("");

  const sectionsRef = useRef<Sec[]>([]);
  /** What the person has typed and the state does not hold yet. */
  const liveRef = useRef(new Map<string, { title: string; html: string }>());
  const deletedRef = useRef<string[]>([]);
  const sectionEls = useRef(new Map<string, HTMLElement>());
  const bodyEls = useRef(new Map<string, HTMLElement>());
  const titleEls = useRef(new Map<string, HTMLTextAreaElement>());
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const focusNewRef = useRef<string | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragRef = useRef<SectionDrag | null>(null);
  const missingRef = useRef(onMissing);
  missingRef.current = onMissing;
  const joinRef = useRef(onJoin);
  joinRef.current = onJoin;

  const setSections = useCallback((next: Sec[]) => {
    sectionsRef.current = next;
    setSectionsState(next);
  }, []);

  const valueOf = useCallback((id: string): { title: string; html: string } | null => {
    const live = liveRef.current.get(id);
    const sec = sectionsRef.current.find((s) => s.id === id);
    if (!sec) return null;
    return { title: live?.title ?? sec.title, html: live?.html ?? sec.html };
  }, []);

  const saver = useMemo(
    () =>
      new PrepSaver({
        slug,
        save: (req, opts) => savePrepDoc(req, opts),
        read: valueOf,
        order: () => sectionsRef.current.map((s) => s.id),
        deleted: () => [...deletedRef.current],
      }),
    [slug, valueOf],
  );

  useEffect(() => {
    const off = saver.subscribe(() => setStatus(saver.status));
    return () => {
      off();
    };
  }, [saver]);

  // Load, and the poll that follows.
  const refresh = useCallback(
    async (first: boolean, signal?: AbortSignal): Promise<void> => {
      const since = saver.tick;
      const startedDrag = dragRef.current?.active ?? false;
      const doc: PrepDoc = await getPrepDoc(slug, signal);
      if (signal?.aborted) return;
      setHead({ title: doc.title, meta: doc.meta[0] ?? "" });
      joinRef.current?.(doc.join ?? "");
      const remote: Sec[] = doc.sections.map((s) => (s.fold ? { id: s.id, title: s.title, html: s.html, fold: true } : { id: s.id, title: s.title, html: s.html }));
      if (first) {
        for (const s of remote) saver.setBase(s.id, s.title, s.html);
        setSections(remote);
        setLoad("ready");
        return;
      }
      const focused = focusedSectionId();
      // The section his cursor is in keeps its own copy only once he has typed
      // in it; one he only clicked into takes a change made elsewhere.
      const typedIn = (id: string): boolean => {
        const live = liveRef.current.get(id);
        const base = saver.getBase(id);
        return !base || (!!live && (live.html !== base.html || live.title !== base.title));
      };
      const busy = (id: string): boolean => (id === focused && typedIn(id)) || saver.busy(id, since);
      const result = mergeRemote({
        local: sectionsRef.current,
        remote,
        busy,
        keepOrder: startedDrag || (dragRef.current?.active ?? false) || saver.layoutBusy(since),
        skip: new Set(deletedRef.current),
      });
      const remoteIds = new Set(remote.map((s) => s.id));
      for (const s of remote) if (!busy(s.id)) saver.setBase(s.id, s.title, s.html);
      for (const s of sectionsRef.current) {
        if (!remoteIds.has(s.id) && !busy(s.id)) saver.dropBase(s.id);
      }
      for (const id of result.taken) liveRef.current.delete(id);
      if (result.changed) setSections([...result.sections]);
    },
    [saver, setSections, slug],
  );

  useEffect(() => {
    const controller = new AbortController();
    liveRef.current.clear();
    deletedRef.current = [];
    setLoad("loading");
    refresh(true, controller.signal).catch((err: unknown) => {
      if ((err as Error).name === "AbortError") return;
      if (err instanceof ApiError && err.status === 404) {
        setLoad("missing");
        missingRef.current?.();
        return;
      }
      setFailed(err instanceof Error ? err.message : "Could not load this agenda.");
      setLoad("error");
    });
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    if (load !== "ready") return;
    let busy = false;
    const run = (): void => {
      if (busy || document.hidden) return;
      busy = true;
      refresh(false)
        .catch(() => undefined)
        .finally(() => {
          busy = false;
        });
    };
    const id = setInterval(run, POLL_MS);
    document.addEventListener("visibilitychange", run);
    window.addEventListener("focus", run);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", run);
      window.removeEventListener("focus", run);
    };
  }, [load, refresh]);

  useEffect(() => {
    if (load !== "ready" || refreshKey === 0) return;
    refresh(false).catch(() => undefined);
  }, [refreshKey, load, refresh]);

  // Flush when the page goes away.
  useEffect(() => {
    const hide = (): void => {
      if (document.visibilityState === "hidden") saver.flush(true);
    };
    const gone = (): void => saver.flush(true);
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", gone);
    return () => {
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("pagehide", gone);
    };
  }, [saver]);

  useLayoutEffect(() => {
    saver.revive();
    return () => {
      dragRef.current?.dispose();
      saver.flush(true);
      saver.dispose();
    };
  }, [saver]);

  useEffect(
    () => () => {
      if (undoTimer.current) clearTimeout(undoTimer.current);
    },
    [],
  );

  // A section just added takes the caret in its heading, text selected.
  useEffect(() => {
    const id = focusNewRef.current;
    if (!id) return;
    const input = titleEls.current.get(id);
    if (!input) return;
    focusNewRef.current = null;
    input.focus({ preventScroll: true });
    input.select();
    sectionEls.current.get(id)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [sections]);

  // ---- section events ----------------------------------------------------------

  const onHtml = useCallback(
    (id: string): void => {
      const body = bodyEls.current.get(id);
      const cur = valueOf(id);
      if (!body || !cur) return;
      liveRef.current.set(id, { title: cur.title, html: sanitizeHtml(body.innerHTML) });
      saver.touchHtml(id);
    },
    [saver, valueOf],
  );

  const onTitle = useCallback(
    (id: string): void => {
      const input = titleEls.current.get(id);
      const cur = valueOf(id);
      if (!input || !cur) return;
      const title = input.value.trim() === "" ? (sectionsRef.current.find((s) => s.id === id)?.title ?? cur.title) : input.value.trim();
      liveRef.current.set(id, { title, html: cur.html });
      saver.touchTitle(id);
    },
    [saver, valueOf],
  );

  const onBlur = useCallback((): void => {
    saver.flush();
  }, [saver]);

  const dismissUndo = useCallback((): void => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
    setUndo(null);
  }, []);

  const onDelete = useCallback(
    (id: string): void => {
      const index = sectionsRef.current.findIndex((s) => s.id === id);
      const cur = valueOf(id);
      if (index < 0 || !cur) return;
      const was = sectionsRef.current[index];
      const sec: Sec = was?.fold ? { id, title: cur.title, html: cur.html, fold: true } : { id, title: cur.title, html: cur.html };
      deletedRef.current = [...deletedRef.current.filter((x) => x !== id), id];
      liveRef.current.delete(id);
      setSections(sectionsRef.current.filter((s) => s.id !== id));
      saver.touchDeleted();
      if (undoTimer.current) clearTimeout(undoTimer.current);
      setUndo({ sec, index, title: sec.title });
      undoTimer.current = setTimeout(() => {
        undoTimer.current = null;
        setUndo(null);
      }, UNDO_MS);
    },
    [saver, setSections, valueOf],
  );

  const onUndo = useCallback((): void => {
    const u = undo;
    if (!u) return;
    dismissUndo();
    deletedRef.current = deletedRef.current.filter((x) => x !== u.sec.id);
    const next = sectionsRef.current.filter((s) => s.id !== u.sec.id);
    next.splice(Math.min(u.index, next.length), 0, u.sec);
    setSections(next);
    // What was typed after the last save goes up again with the restore.
    saver.touchHtml(u.sec.id);
    saver.touchTitle(u.sec.id);
    saver.touchDeleted();
  }, [dismissUndo, saver, setSections, undo]);

  const addSection = useCallback((): void => {
    const id = freshSectionId(new Set(sectionsRef.current.map((s) => s.id)));
    const sec: Sec = { id, title: NEW_SECTION_TITLE, html: NEW_SECTION_HTML };
    focusNewRef.current = id;
    setSections([...sectionsRef.current, sec]);
    saver.touchNew(id);
  }, [saver, setSections]);

  // ---- drag --------------------------------------------------------------------

  const saverRef = useRef(saver);
  saverRef.current = saver;
  if (!dragRef.current) {
    const host: DragHost = {
      root: () => scrollRef.current,
      list: () => listRef.current,
      section: (id) => sectionEls.current.get(id) ?? null,
      order: () => sectionsRef.current.map((s) => s.id),
      preview: (order, id) => setDrag(order && id ? { order, id } : null),
      drop: (order) => {
        const byId = new Map(sectionsRef.current.map((s) => [s.id, s]));
        setSections(order.map((id) => byId.get(id)).filter((s): s is Sec => !!s));
        saverRef.current.touchOrder();
      },
    };
    dragRef.current = new SectionDrag(host);
  }

  const onHold = useCallback((e: ReactPointerEvent, id: string, hold?: number): void => {
    dragRef.current?.start(e, id, hold);
  }, []);

  const shown = useMemo<Sec[]>(() => {
    if (!drag) return sections;
    const byId = new Map(sections.map((s) => [s.id, s]));
    return drag.order.map((id) => byId.get(id)).filter((s): s is Sec => !!s);
  }, [drag, sections]);

  const setSectionEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) sectionEls.current.set(id, el);
    else sectionEls.current.delete(id);
  }, []);
  const setBodyEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) bodyEls.current.set(id, el);
    else bodyEls.current.delete(id);
  }, []);
  const setTitleEl = useCallback((id: string, el: HTMLTextAreaElement | null) => {
    if (el) titleEls.current.set(id, el);
    else titleEls.current.delete(id);
  }, []);

  // ---- render ------------------------------------------------------------------

  if (load === "loading") return <div className="pe pe--state" aria-busy="true">Loading</div>;
  if (load === "missing") return <div className="pe pe--state">Nothing is at this address</div>;
  if (load === "error") {
    return (
      <div className="pe pe--state">
        <p>{failed || "Could not load this agenda."}</p>
        <button
          type="button"
          className="pe__retry"
          onClick={() => {
            setLoad("loading");
            refresh(true).catch((err: unknown) => {
              setFailed(err instanceof Error ? err.message : "Could not load this agenda.");
              setLoad("error");
            });
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  const text = statusText(status);
  return (
    <div className="pe">
      <div className="pe__top">
        {bare ? null : (
          <div className="pe__heading">
            <h1 className="pe__name">{head.title}</h1>
            {head.meta ? <p className="pe__meta">{head.meta}</p> : null}
          </div>
        )}
        <span className={status === "failed" ? "pe__status pe__status--failed" : "pe__status"} role="status" aria-live="polite">
          {text}
        </span>
      </div>
      <div className="pe__scroll" ref={scrollRef}>
        <div className="pe__list" ref={listRef}>
          {shown.map((s) => (
            <PrepSection
              key={s.id}
              sec={s}
              lifted={drag?.id === s.id}
              setSection={setSectionEl}
              setBody={setBodyEl}
              setTitle={setTitleEl}
              onHtml={onHtml}
              onTitle={onTitle}
              onBlur={onBlur}
              onDelete={onDelete}
              onHold={onHold}
            />
          ))}
        </div>
        <button type="button" className="pe__add" onClick={addSection}>
          <PlusIcon size={18} />
          <span>Add section</span>
        </button>
      </div>
      {undo ? (
        <div className="pe__toast" role="status">
          <span>Deleted {undo.title}</span>
          <button type="button" className="pe__undo" onClick={onUndo}>
            Undo
          </button>
        </div>
      ) : null}
    </div>
  );
}
